import { item, type NavItem } from "./navigation-item";
import { SETTINGS_NAVIGATION_MODULES } from "./navigation-settings-modules";

/** The settings hub: the organization, its people, lines, front desk, care lists and the person's
 * own screens, then the module screens. */
export const SETTINGS_NAVIGATION: readonly NavItem[] = [
  /* Grace's tablets at the door, the badge printers they print to and the names
     the desk wants warned about. Her own heading: the front desk heading is Sarah's. */
  item("visitor-kiosk", "Visitor kiosk", "/settings/visitor-kiosk", "visitors", "visitor:manage", {
    surface: "settings",
    scope: "facility",
    settingsCategory: "Visitors",
  }),
  item("fax-settings", "Fax", "/settings/fax", "communications", "crm:manage", {
    surface: "settings",
    scope: "shared",
    settingsCategory: "Phone & messaging",
  }),
  // Configuration, not operations: the sidebar keeps the call log and its analytics (messages are a
  // tab inside the log), and everything that tunes the agent lives in the settings hub instead. The
  // URLs are unchanged, so a bookmark still lands where it always did.
  item("sarah-staff", "Front desk staff", "/sarah/staff", "front-desk", "frontdesk:manage", {
    surface: "settings",
    scope: "facility",
    settingsCategory: "Front desk",
  }),
  item("sarah-agent", "Front desk agent", "/sarah/agent", "front-desk", "frontdesk:manage", {
    surface: "settings",
    scope: "facility",
    settingsCategory: "Front desk",
  }),
  /* A rehearsal and a nightly queue of proposals, not settings: Sarah's Set up page holds them. */
  item("sarah-agent-test", "Agent test", "/sarah/agent-test", "front-desk", "frontdesk:manage", {
    surface: "hidden",
  }),
  item(
    "sarah-suppression",
    "Screened calls",
    "/sarah/suppression",
    "front-desk",
    "frontdesk:manage",
    {
      surface: "settings",
      scope: "facility",
      settingsCategory: "Front desk",
    },
  ),
  item(
    "sarah-escalation",
    "Priority calls",
    "/sarah/escalation",
    "front-desk",
    "frontdesk:manage",
    {
      surface: "settings",
      scope: "facility",
      settingsCategory: "Front desk",
    },
  ),
  item("sarah-optimizer", "Agent optimizer", "/sarah/optimizer", "front-desk", "frontdesk:manage", {
    surface: "hidden",
  }),
  item(
    "sarah-facility-settings",
    "Phone",
    "/sarah/facility-settings",
    "front-desk",
    "frontdesk:manage",
    // A facility's number page on the Phone list; the old address still lands.
    { surface: "hidden", scope: "facility" },
  ),
  item(
    "sarah-org-settings",
    "Front desk credentials",
    "/sarah/credentials",
    "front-desk",
    "frontdesk:manage",
    {
      surface: "settings",
      scope: "inherited",
      settingsCategory: "Phone & messaging",
    },
  ),
  item("phone-number-bank", "Phone", "/sarah/numbers", "communications", "settings:manage", {
    surface: "settings",
    scope: "organization",
    settingsCategory: "Phone & messaging",
  }),
  item("checklists", "Checklists", "/settings/checklists", "administration", "settings:read", {
    surface: "settings",
    scope: "shared",
    settingsCategory: "Care",
  }),
  /* One list for every organization on the platform, so it is the console's to keep. */
  item(
    "assistive-devices",
    "Assistive devices",
    "/settings/assistive-devices",
    "administration",
    "platform:read",
    { surface: "settings", scope: "platform", settingsCategory: "Platform", platformOnly: true },
  ),
  item("tags", "Tags", "/settings/tags", "administration", "settings:read", {
    surface: "settings",
    scope: "shared",
    settingsCategory: "Care",
  }),
  item(
    "activation",
    "Activation requests",
    "/settings/activation",
    "administration",
    "settings:read",
    {
      surface: "settings",
      scope: "organization",
      settingsCategory: "Organization",
    },
  ),
  item(
    "demographic-lists",
    "Resident lists",
    "/settings/demographic-lists",
    "administration",
    "settings:read",
    {
      surface: "settings",
      scope: "facility",
      settingsCategory: "Care",
    },
  ),
  item(
    "incident-categories",
    "Incident categories",
    "/settings/incident-categories",
    "care",
    "incident:manage",
    {
      surface: "settings",
      scope: "shared",
      settingsCategory: "Care",
    },
  ),
  item("menu", "Navigation & favorites", "/settings/menu", "administration", "settings:read", {
    surface: "settings",
    scope: "personal",
    settingsCategory: "Personal",
  }),
  item(
    "organization",
    "Organization",
    "/settings/organization",
    "administration",
    "organization:update",
    {
      surface: "settings",
      scope: "organization",
      settingsCategory: "Organization",
    },
  ),
  // Everyone with a login, employees and not. The Employees register (reached
  // from HR) is employees only, so this is where the rest of the people are.
  item("user-management", "User management", "/settings/users", "administration", "staff:read", {
    surface: "settings",
    scope: "organization",
    settingsCategory: "Users & access",
  }),
  item(
    "role-management",
    "Roles & permissions",
    "/settings/roles",
    "administration",
    "organization:manage-roles",
    { surface: "settings", scope: "organization", settingsCategory: "Users & access" },
  ),
  item("billing", "Billing", "/settings/billing", "administration", "organization:manage-billing", {
    surface: "settings",
    scope: "organization",
    settingsCategory: "Billing & plan",
  }),
  item("facilities", "Facilities", "/settings/facilities", "administration", "facility:read", {
    surface: "settings",
    scope: "organization",
    settingsCategory: "Organization",
  }),
  /* The building in view's own record and settings. The organization's list above only adds,
     opens, deactivates and reactivates buildings. */
  item(
    "facility-profile",
    "Facility profile",
    "/settings/facility",
    "administration",
    "facility:read",
    { surface: "settings", scope: "facility", settingsCategory: "Facility" },
  ),
  // Your own page. You owns it, and Settings lists it as well, so it is one
  // step away from either place somebody looks for it.
  item("settings", "My profile", "/settings/profile", "administration", "settings:read", {
    surface: "settings",
    scope: "personal",
    settingsCategory: "Personal",
  }),
  item("api-keys", "API keys", "/settings/api-keys", "administration", "settings:manage", {
    surface: "settings",
    scope: "organization",
    settingsCategory: "Organization",
  }),
  ...SETTINGS_NAVIGATION_MODULES,
];
